import { useEffect, useRef } from "react"
import type { DbObject } from "@/db/schema"
import { ObjectStatus } from "@/db/schema"
import { useViewContext } from "@/app/stores/viewContext"

/**
 * Auto-close the task detail when the task is deleted, moved out of
 * the current view, or otherwise becomes unavailable.
 *
 * Watches the live `task` object for changes and calls `onClose()`
 * silently (no toast) when:
 * - Task is soft-deleted (`isDeleted === 1`) — except in Trash view
 * - Task no longer exists (parent unmounts, but this catches edge cases)
 *
 * Does NOT auto-close on completion/cancellation — the user may want to
 * see the completed state and undo.
 *
 * §8.11 / §32 — useTaskDetailAutoClose
 */
export function useTaskDetailAutoClose(
  task: DbObject | null | undefined,
  onClose: () => void,
) {
  const viewId = useViewContext((s) => s.viewId)

  // Track the initial task ID to detect when the prop changes to a
  // completely different task (or null).
  const initialIdRef = useRef(task?.id)

  useEffect(() => {
    // On first mount, just record the ID
    if (initialIdRef.current === undefined && task?.id) {
      initialIdRef.current = task.id
    }
  }, [task?.id])

  useEffect(() => {
    if (!task) return

    // In Trash view, tasks are already deleted — don't auto-close them.
    // In all other views, auto-close if the task is soft-deleted.
    if (task.isDeleted === 1 && viewId !== "trash") {
      onClose()
      return
    }

    // If the task status changed to Hidden (e.g. admin action), auto-close
    if (task.status === ObjectStatus.Hidden) {
      onClose()
    }
  }, [task, task?.isDeleted, task?.status, onClose, viewId])
}
